import { allIsraelSettlements } from '../utils/israelSettlements';
import { normalizeVoiceText } from './matchVoiceSearchCity';

export type VoiceCitySuggestion = {
  cityHe: string;
  cityEn: string;
  /** Phrase that scored (EN or HE). */
  matchedPhrase: string;
  /** Lower is better (0 = exact). */
  distance: number;
};

const SPOKEN_CITY_ALIASES: Array<{ phrase: string; he: string }> = [
  { phrase: 'ramat gan', he: 'רמת גן' },
  { phrase: 'ramatgan', he: 'רמת גן' },
  { phrase: 'רמת גן', he: 'רמת גן' },
  { phrase: 'tel aviv', he: 'תל אביב - יפו' },
  { phrase: 'telaviv', he: 'תל אביב - יפו' },
  { phrase: 'תל אביב', he: 'תל אביב - יפו' },
];

function compactVoice(value: string): string {
  return normalizeVoiceText(value).replace(/\s+/g, '');
}

/** Classic Levenshtein distance (insert / delete / substitute). */
export function voiceEditDistance(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  const la = a.length;
  const lb = b.length;
  if (la === 0) {
    return lb;
  }
  if (lb === 0) {
    return la;
  }
  const prev = new Array<number>(lb + 1);
  const cur = new Array<number>(lb + 1);
  for (let j = 0; j <= lb; j += 1) {
    prev[j] = j;
  }
  for (let i = 1; i <= la; i += 1) {
    cur[0] = i;
    const ca = a[i - 1];
    for (let j = 1; j <= lb; j += 1) {
      const cost = ca === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(
        (prev[j] ?? 0) + 1,
        (cur[j - 1] ?? 0) + 1,
        (prev[j - 1] ?? 0) + cost,
      );
    }
    for (let j = 0; j <= lb; j += 1) {
      prev[j] = cur[j] ?? 0;
    }
  }
  return prev[lb] ?? lb;
}

function maxAllowedDistance(queryLen: number, phraseLen: number): number {
  const n = Math.max(queryLen, phraseLen);
  if (n <= 4) {
    return 1;
  }
  if (n <= 7) {
    return 2;
  }
  return 3;
}

type PhraseEntry = { phrase: string; compact: string; cityHe: string; cityEn: string };

let phraseCatalog: PhraseEntry[] | null = null;

function getPhraseCatalog(): PhraseEntry[] {
  if (phraseCatalog) {
    return phraseCatalog;
  }
  const byHe = new Map(allIsraelSettlements().map((s) => [s.he.trim(), s]));
  const out: PhraseEntry[] = [];
  const seen = new Set<string>();
  const push = (phrase: string, cityHe: string, cityEn: string) => {
    const normalized = normalizeVoiceText(phrase);
    if (normalized.length < 3) {
      return;
    }
    const key = `${normalized}\0${cityHe}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push({
      phrase: normalized,
      compact: compactVoice(normalized),
      cityHe,
      cityEn,
    });
  };

  for (const settlement of allIsraelSettlements()) {
    if (settlement.he.includes('שבט')) {
      continue;
    }
    const cityHe = settlement.he.trim();
    const cityEn = settlement.en.trim();
    push(cityHe, cityHe, cityEn);
    push(cityEn, cityHe, cityEn);
    push(cityHe.split(/\s*[-–]\s*/)[0] ?? '', cityHe, cityEn);
    push(cityEn.split(/\s*[-–]\s*/)[0] ?? '', cityHe, cityEn);
  }
  for (const alias of SPOKEN_CITY_ALIASES) {
    const settlement = byHe.get(alias.he);
    if (settlement) {
      push(alias.phrase, settlement.he.trim(), settlement.en.trim());
    }
  }
  phraseCatalog = out;
  return out;
}

function scoreTokenPairAlignment(query: string, phrase: string): number | null {
  const qt = normalizeVoiceText(query).split(/\s+/).filter(Boolean);
  const pt = normalizeVoiceText(phrase).split(/\s+/).filter(Boolean);
  if (qt.length < 2 || pt.length < 2 || qt.length !== pt.length) {
    return null;
  }
  let total = 0;
  for (let i = 0; i < qt.length; i += 1) {
    const d = voiceEditDistance(qt[i] ?? '', pt[i] ?? '');
    // “ramada”→“ramat” and “ghana”→“gan” are each 2 edits (speech STT noise).
    if (d > 2) {
      return null;
    }
    total += d;
  }
  return total <= 4 ? total : null;
}

function scoreQueryAgainstPhrase(query: string, entry: PhraseEntry): number | null {
  const q = normalizeVoiceText(query);
  const qc = compactVoice(q);
  if (!q || qc.length < 3) {
    return null;
  }
  if (q === entry.phrase || qc === entry.compact) {
    return 0;
  }
  const spaced = voiceEditDistance(q, entry.phrase);
  const glued = voiceEditDistance(qc, entry.compact);
  const paired = scoreTokenPairAlignment(q, entry.phrase);
  const distance = Math.min(spaced, glued, paired ?? Number.POSITIVE_INFINITY);
  if (!Number.isFinite(distance)) {
    return null;
  }
  const allowed = Math.max(
    maxAllowedDistance(qc.length, entry.compact.length),
    paired != null ? 4 : 0,
  );
  if (distance > allowed) {
    return null;
  }
  // Prefer similar length (ramadan↔ramatgan over random short towns).
  const lengthGap = Math.abs(qc.length - entry.compact.length);
  return distance + lengthGap * 0.15;
}

/**
 * Rank Israel cities/settlements near a spoken leftover (“ramadan”, “ramada ghana”).
 * Exact matches score 0; typical voice near-misses land within distance 1–2.
 */
export function suggestVoiceSearchCities(
  query: string,
  options?: { limit?: number },
): VoiceCitySuggestion[] {
  const q = normalizeVoiceText(query);
  if (!q) {
    return [];
  }
  const limit = options?.limit ?? 5;
  const bestByCity = new Map<string, VoiceCitySuggestion>();

  for (const entry of getPhraseCatalog()) {
    const distance = scoreQueryAgainstPhrase(q, entry);
    if (distance == null) {
      continue;
    }
    const prev = bestByCity.get(entry.cityHe);
    if (prev && prev.distance <= distance) {
      continue;
    }
    bestByCity.set(entry.cityHe, {
      cityHe: entry.cityHe,
      cityEn: entry.cityEn,
      matchedPhrase: entry.phrase,
      distance,
    });
  }

  return Array.from(bestByCity.values())
    .sort((a, b) => a.distance - b.distance || a.cityHe.localeCompare(b.cityHe, 'he'))
    .slice(0, limit);
}

/** Strong single winner — safe to auto-apply as the city. */
export function pickConfidentVoiceCitySuggestion(
  suggestions: VoiceCitySuggestion[],
): VoiceCitySuggestion | null {
  if (suggestions.length === 0) {
    return null;
  }
  const best = suggestions[0];
  if (!best || best.distance > 2.5) {
    return null;
  }
  const second = suggestions[1];
  // Ask only when another city is nearly as good (clear tie).
  if (second && second.distance - best.distance < 0.35 && second.distance <= 2.5) {
    return null;
  }
  return best;
}

export function formatVoiceCitySuggestionLabel(
  suggestion: VoiceCitySuggestion,
  language: 'he' | 'en',
): string {
  if (language === 'he') {
    return suggestion.cityHe.split(/\s*[-–]\s*/)[0]?.trim() || suggestion.cityHe;
  }
  return suggestion.cityEn.split(/\s*[-–]\s*/)[0]?.trim() || suggestion.cityEn;
}
